import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  enableNetwork,
  disableNetwork,
  where,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db } from './firebase';
import { Patient, ReportData } from '../types/types';
import { TestTemplate } from '../constants/testTemplates';

// Collections
const REPORTS_COLLECTION = 'reports';
const PATIENTS_COLLECTION = 'patients';
const TEMPLATES_COLLECTION = 'testTemplates';
// PDF Themes collection
const PDF_THEMES_COLLECTION = 'pdfThemes';
const BILLS_COLLECTION = 'bills';

// Bills CRUD operations
export const addBill = async (billData: any): Promise<string> => {
  try {
    if (!billData.labId) {
      throw new Error('labId is required to add a bill');
    }

    const bill = {
      ...billData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, BILLS_COLLECTION), bill);
    return docRef.id;
  } catch (error) {
    console.error('Error adding bill:', error);
    throw error;
  }
};

export const getBills = async (labId?: string): Promise<any[]> => {
  try {
    let q;
    if (labId) {
      q = query(
        collection(db, BILLS_COLLECTION),
        where('labId', '==', labId),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(collection(db, BILLS_COLLECTION), orderBy('createdAt', 'desc'));
    }
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting bills:', error);
    throw error;
  }
};

export const subscribeToBills = (callback: (bills: any[]) => void, labId?: string, limitCount = 50) => {
  let q;
  if (labId) {
    q = query(
      collection(db, BILLS_COLLECTION),
      where('labId', '==', labId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
  } else {
    q = query(collection(db, BILLS_COLLECTION), orderBy('createdAt', 'desc'), limit(limitCount));
  }

  return onSnapshot(q,
    (querySnapshot) => {
      const bills = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(bills);
    },
    (error) => {
      console.error('Error subscribing to bills:', error);
    }
  );
};

// Reports CRUD operations
export const addReport = async (reportData: Omit<ReportData, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    if (!reportData.labId) {
      throw new Error('labId is required to add a report');
    }

    const report = {
      ...reportData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, REPORTS_COLLECTION), report);
    console.log('Report added with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding report:', error);
    throw error;
  }
};

export const getReports = async (labId?: string): Promise<ReportData[]> => {
  try {
    let q;
    if (labId) {
      q = query(
        collection(db, REPORTS_COLLECTION),
        where('labId', '==', labId),
        orderBy('createdAt', 'desc')
      );
    } else {
      // Fallback for backward compatibility or admin view
      q = query(
        collection(db, REPORTS_COLLECTION),
        orderBy('createdAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...(doc.data() as Omit<ReportData, 'id'>)
    })) as ReportData[];
  } catch (error) {
    console.error('Error getting reports:', error);
    throw error;
  }
};

export const subscribeToReports = (callback: (reports: ReportData[]) => void, labId?: string, limitCount = 50) => {
  let q;
  if (labId) {
    q = query(
      collection(db, REPORTS_COLLECTION),
      where('labId', '==', labId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
  } else {
    q = query(
      collection(db, REPORTS_COLLECTION),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
  }

  return onSnapshot(q,
    (querySnapshot) => {
      const reports = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as Omit<ReportData, 'id'>)
      })) as ReportData[];
      callback(reports);
    },
    (error) => {
      console.error('Firestore subscription error (will retry automatically):', error);
      // Firestore will automatically retry connections
      // This is likely a temporary network issue
    }
  );
};

export const updateReport = async (reportId: string, updates: Partial<ReportData>): Promise<void> => {
  try {
    const reportRef = doc(db, REPORTS_COLLECTION, reportId);
    await updateDoc(reportRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating report:', error);
    throw error;
  }
};

export const deleteReport = async (reportId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, REPORTS_COLLECTION, reportId));
  } catch (error) {
    console.error('Error deleting report:', error);
    throw error;
  }
};

// Patients CRUD operations
export const addPatient = async (patientData: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    if (!patientData.labId) {
      throw new Error('labId is required to add a patient');
    }

    const patient = {
      ...patientData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, PATIENTS_COLLECTION), patient);
    return docRef.id;
  } catch (error) {
    console.error('Error adding patient:', error);
    throw error;
  }
};

export const getPatients = async (labId?: string): Promise<Patient[]> => {
  try {
    let q;
    if (labId) {
      q = query(
        collection(db, PATIENTS_COLLECTION),
        where('labId', '==', labId),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(
        collection(db, PATIENTS_COLLECTION),
        orderBy('createdAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Patient[];
  } catch (error) {
    console.error('Error getting patients:', error);
    throw error;
  }
};

export const deletePatient = async (patientId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, PATIENTS_COLLECTION, patientId));
  } catch (error) {
    console.error('Error deleting patient:', error);
    throw error;
  }
};

export const subscribeToPatients = (callback: (patients: Patient[]) => void, labId?: string, limitCount = 50) => {
  let q;
  if (labId) {
    q = query(
      collection(db, PATIENTS_COLLECTION),
      where('labId', '==', labId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
  } else {
    q = query(
      collection(db, PATIENTS_COLLECTION),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
  }

  return onSnapshot(q,
    (querySnapshot) => {
      const patients = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Patient[];
      callback(patients);
    },
    (error) => {
      console.error('Error subscribing to patients:', error);
    }
  );
};

// Test Templates operations
export const saveTestTemplates = async (labId: string, bloodTests: TestTemplate[], urineTests: TestTemplate[]): Promise<void> => {
  try {
    const templatesData = {
      bloodTests,
      urineTests,
      updatedAt: Timestamp.now(),
      labId
    };

    const templateRef = doc(db, TEMPLATES_COLLECTION, labId);
    // Use setDoc with merge: true or just setDoc since we want to overwrite/create
    // updateDoc fails if doc doesn't exist. setDoc is better here.
    const { setDoc } = await import('firebase/firestore');
    await setDoc(templateRef, templatesData, { merge: true });
  } catch (error) {
    console.error('Error saving templates:', error);
    throw error;
  }
};

export const getTestTemplatesFromFirestore = async (labId?: string): Promise<{ bloodTests: TestTemplate[], urineTests: TestTemplate[] } | null> => {
  try {
    const { getDoc } = await import('firebase/firestore');

    // 1. Try fetching lab-specific templates
    if (labId) {
      const labTemplateRef = doc(db, TEMPLATES_COLLECTION, labId);
      const labTemplateSnap = await getDoc(labTemplateRef);

      if (labTemplateSnap.exists()) {
        const data = labTemplateSnap.data();
        return {
          bloodTests: data.bloodTests || [],
          urineTests: data.urineTests || []
        };
      }
    }

    // 2. Fallback to default templates
    const defaultTemplateRef = doc(db, TEMPLATES_COLLECTION, 'default');
    const defaultTemplateSnap = await getDoc(defaultTemplateRef);

    if (defaultTemplateSnap.exists()) {
      const data = defaultTemplateSnap.data();
      return {
        bloodTests: data.bloodTests || [],
        urineTests: data.urineTests || []
      };
    }

    // 3. Fallback to collection query (legacy behavior, maybe remove later)
    const templateDoc = await getDocs(collection(db, TEMPLATES_COLLECTION));
    if (!templateDoc.empty) {
      // Check if any doc is 'default' or just take the first one?
      // Let's just return null to force local defaults if 'default' doc is missing
      // But to be safe for existing data:
      const data = templateDoc.docs[0].data();
      return {
        bloodTests: data.bloodTests || [],
        urineTests: data.urineTests || []
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting templates:', error);
    return null;
  }
};

// PDF Themes CRUD
export interface PdfThemeRecord {
  id?: string;
  name?: string;
  // Legacy base64 fields (kept for backward compatibility)
  logoDataUrl?: string;
  microscopeDataUrl?: string;
  technicianSignDataUrl?: string;
  pathologistSignDataUrl?: string;

  // Preferred remote Storage URL + filename
  logoUrl?: string;
  logoFilename?: string;
  microscopeUrl?: string;
  microscopeFilename?: string;
  technicianSignUrl?: string;
  technicianSignFilename?: string;
  pathologistSignUrl?: string;
  pathologistSignFilename?: string;
  clinicName?: string;
  addressLines?: string[];
  contactEmail?: string;
  footerLeftText?: string;
  footerRightText?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  labId?: string;
  templateId?: string;
}

export const listPdfThemes = async (labId?: string): Promise<PdfThemeRecord[]> => {
  try {
    let q;
    if (labId) {
      q = query(
        collection(db, PDF_THEMES_COLLECTION),
        where('labId', '==', labId),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(collection(db, PDF_THEMES_COLLECTION), orderBy('createdAt', 'desc'));
    }
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({ id: d.id, ...(d.data() as PdfThemeRecord) }));
  } catch (error) {
    console.error('Error listing PDF themes:', error);
    return [];
  }
};

export const getPdfTheme = async (themeId: string): Promise<PdfThemeRecord | null> => {
  try {
    // Fall back to collection scan to find the theme by id
    const snap = await getDocs(collection(db, PDF_THEMES_COLLECTION));
    const found = snap.docs.find(d => d.id === themeId);
    if (found) return { id: found.id, ...(found.data() as PdfThemeRecord) };
    return null;
  } catch (error) {
    console.error('Error getting PDF theme:', error);
    return null;
  }
};

export const upsertPdfTheme = async (theme: PdfThemeRecord): Promise<string | null> => {
  try {
    if (theme.id) {
      const themeRef = doc(db, PDF_THEMES_COLLECTION, theme.id);
      await updateDoc(themeRef, { ...theme, updatedAt: Timestamp.now() });
      return theme.id;
    }

    if (!theme.labId) {
      console.warn('Upserting theme without labId');
    }

    const data = { ...theme, createdAt: Timestamp.now(), updatedAt: Timestamp.now() };
    const docRef = await addDoc(collection(db, PDF_THEMES_COLLECTION), data);
    return docRef.id;
  } catch (error) {
    console.error('Error upserting PDF theme:', error);
    return null;
  }
};

export const deletePdfTheme = async (themeId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, PDF_THEMES_COLLECTION, themeId));
  } catch (error) {
    console.error('Error deleting PDF theme:', error);
    throw error;
  }
};

// Offline support
export const enableOfflineSupport = async (): Promise<void> => {
  try {
    await disableNetwork(db);
    console.log('Offline mode enabled');
  } catch (error) {
    console.error('Failed to enable offline mode:', error);
  }
};

export const enableOnlineSupport = async (): Promise<void> => {
  try {
    await enableNetwork(db);
    console.log('Online mode enabled');
  } catch (error) {
    console.error('Failed to enable online mode:', error);
  }
};

// Local storage helpers for offline functionality
const OFFLINE_REPORTS_KEY = 'offline_reports';

export interface OfflineReportData extends ReportData {
  id: string;
  isOffline: boolean;
}

export const saveReportOffline = (report: Omit<ReportData, 'id'>): void => {
  try {
    const offlineReports = getOfflineReports();
    const reportWithId = {
      ...report,
      id: `offline_${Date.now()}`,
      isOffline: true,
      createdAt: Timestamp.now()
    };

    offlineReports.push(reportWithId as OfflineReportData);
    localStorage.setItem(OFFLINE_REPORTS_KEY, JSON.stringify(offlineReports));
  } catch (error) {
    console.error('Error saving report offline:', error);
  }
};

export const getOfflineReports = (): OfflineReportData[] => {
  try {
    const stored = localStorage.getItem(OFFLINE_REPORTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting offline reports:', error);
    return [];
  }
};

export const syncOfflineReports = async (): Promise<void> => {
  try {
    const offlineReports = getOfflineReports();

    for (const report of offlineReports) {
      const { id, isOffline, ...reportData } = report;
      // Using variables to prevent unused warnings
      void id;
      void isOffline;
      await addReport(reportData as Omit<ReportData, 'id'>);
    }

    // Clear offline reports after successful sync
    localStorage.removeItem(OFFLINE_REPORTS_KEY);
    console.log(`Synced ${offlineReports.length} offline reports`);
  } catch (error) {
    console.error('Error syncing offline reports:', error);
  }
};

// Export the types needed by DatabaseContext
export type { ReportData, Patient } from '../types/types';